import React, { useEffect, useState, useCallback } from "react";
import api, { API, getErrorMessage } from "@/lib/api";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import { Smartphone, Download, Check, X, ShieldCheck, AlertTriangle, IndianRupee } from "lucide-react";

const STATUSES = ["", "verification", "approved", "done", "rejected"];
const OPS = [["", "All operators"], ["jio", "Jio"], ["airtel", "Airtel"], ["vi", "Vi"], ["bsnl", "BSNL"]];

function VerifRow({ ok, label, value }) {
  return (
    <div className={`flex items-center justify-between text-xs py-0.5 ${ok === false ? "text-red-600" : ok === true ? "text-green-700" : "text-thali-ink/70"}`}>
      <span>{label}</span><span className="font-bold">{value}</span>
    </div>
  );
}

export default function AdminRechargeClaims() {
  const [d, setD] = useState(null);
  const [status, setStatus] = useState("");
  const [operator, setOperator] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/recharge-claims", { params: { status, operator } });
    setD(data);
  }, [status, operator]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <Loader />;

  const approve = async (id) => {
    try { const { data } = await api.post(`/admin/recharge-claims/${id}/approve`); if (data.budget_exceeded) toast.warning(data.message); else toast.success("Approved"); load(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection?", "");
    if (reason === null) return;
    try { await api.post(`/admin/recharge-claims/${id}/reject`, { reason }); toast.success("Rejected"); load(); }
    catch { toast.error("Failed"); }
  };
  const fulfil = async (id) => {
    const ref = window.prompt("Enter the recharge reference id:", "");
    if (!ref) return;
    try { await api.post(`/admin/recharge-claims/${id}/fulfil`, { reference_id: ref }); toast.success("Marked done ✅"); load(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  const offerAlt = async (id) => {
    if (!window.confirm("Offer the postpaid alternative (extra free subscription months)?")) return;
    try { const { data } = await api.post(`/admin/recharge-claims/${id}/offer-alt`, {}); toast.success(`+${data.months} months granted`); load(); }
    catch { toast.error("Failed"); }
  };

  const b = d.budget || {};
  const budgetPct = b.cap ? Math.min(100, Math.round((b.spent / b.cap) * 100)) : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl md:text-3xl font-heading font-extrabold flex items-center gap-2"><Smartphone className="w-7 h-7 text-thali-saffron" /> Recharge Claims</h1>
        <a href={`${API}/admin/recharge-claims/export`} className="btn-ghost text-sm" data-testid="recharge-export"><Download className="w-4 h-4" /> Export Excel</a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Under verification", d.counts.verification, "#EA580C"], ["Approved", d.counts.approved, "#2563EB"], ["Done", d.counts.done, "#0B6B3A"], ["Rejected", d.counts.rejected, "#DB2777"]].map(([l, v, c], i) => (
          <div key={i} className="card-thali p-4" data-testid={`recharge-count-${i}`}>
            <div className="text-xs text-thali-ink/60">{l}</div>
            <div className="font-num font-extrabold text-2xl" style={{ color: c }}>{v}</div>
          </div>
        ))}
      </div>

      <div className="card-thali" data-testid="recharge-budget">
        <div className="flex items-center justify-between text-sm">
          <span className="font-heading font-bold flex items-center gap-1"><IndianRupee className="w-4 h-4" /> Monthly recharge budget</span>
          <span className="font-num font-bold">₹{b.spent} / ₹{b.cap}</span>
        </div>
        <div className="h-2 bg-thali-cream rounded-full mt-2 overflow-hidden"><div className="h-full bg-thali-saffron" style={{ width: `${budgetPct}%` }} /></div>
        {!d.provider_configured && <div className="text-xs text-thali-ink/50 mt-2">Automatic recharge provider not configured — fulfil manually (enter reference id). {d.auto_enabled ? "Auto mode ON." : ""}</div>}
      </div>

      <div className="flex gap-2 flex-wrap">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-thali w-auto text-sm" data-testid="recharge-filter-status">
          {STATUSES.map((s) => <option key={s} value={s}>{s ? s[0].toUpperCase() + s.slice(1) : "All statuses"}</option>)}
        </select>
        <select value={operator} onChange={(e) => setOperator(e.target.value)} className="input-thali w-auto text-sm" data-testid="recharge-filter-operator">
          {OPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {d.claims.length === 0 ? <div className="card-thali text-sm text-thali-ink/50">No recharge claims yet.</div> : (
        <div className="space-y-3">
          {d.claims.map((c) => {
            const v = c.verification || {};
            return (
              <div key={c.id} className="card-thali" data-testid={`recharge-claim-${c.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-bold">{c.name} <span className="text-thali-ink/50 font-normal text-sm">{c.mobile}</span></div>
                    <div className="text-sm text-thali-ink/70">{c.operator_name} · {c.circle} · {c.is_prepaid ? "Prepaid" : "Postpaid"}</div>
                    <div className="text-sm">{c.plan?.name} — <b>₹{c.plan?.amount}</b> · {c.plan?.validity} days</div>
                    {c.reference_id && <div className="text-xs text-thali-green mt-1">Ref: {c.reference_id} · {(c.recharge_date || "").slice(0, 10)}</div>}
                    {c.note && <div className="text-xs text-thali-saffron mt-1">{c.note}</div>}
                    {c.reason && <div className="text-xs text-red-600 mt-1">Rejected: {c.reason}</div>}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-bold ${c.status === "done" ? "bg-green-100 text-green-700" : c.status === "approved" ? "bg-blue-100 text-blue-700" : c.status === "rejected" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{c.status}</span>
                </div>

                <div className="mt-3 rounded-xl bg-thali-cream/60 p-3">
                  <div className="text-xs font-bold flex items-center gap-1 mb-1"><ShieldCheck className="w-4 h-4" /> Verification summary {v.auto_ok ? <span className="text-green-700">(auto-checks pass)</span> : <span className="text-red-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> review needed</span>}</div>
                  <div className="grid md:grid-cols-2 gap-x-6">
                    <VerifRow label="Valid referrals" value={v.valid_count} />
                    <VerifRow ok={v.calculated_ok} label={`Referred who calculated (≥${v.min_calculated})`} value={v.calculated} />
                    <VerifRow ok={v.account_age_ok} label={`Account age (≥${v.min_account_age_days}d)`} value={`${v.account_age_days}d`} />
                    <VerifRow ok={v.refunded === 0} label="Refunded referrals" value={v.refunded} />
                    <VerifRow label="Max sign-ups / same IP" value={v.max_same_ip} />
                    <VerifRow label="Max sign-ups / same device" value={v.max_same_device} />
                  </div>
                </div>

                {c.status !== "done" && c.status !== "rejected" && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {c.status === "verification" && <button onClick={() => approve(c.id)} className="btn-3d-green text-sm" data-testid={`recharge-approve-${c.id}`}><Check className="w-4 h-4" /> Approve</button>}
                    {c.status === "approved" && <button onClick={() => fulfil(c.id)} className="btn-3d-green text-sm" data-testid={`recharge-fulfil-${c.id}`}><Check className="w-4 h-4" /> Enter reference & mark done</button>}
                    {!c.is_prepaid && <button onClick={() => offerAlt(c.id)} className="btn-3d-turmeric text-sm" data-testid={`recharge-alt-${c.id}`}>Offer subscription months</button>}
                    <button onClick={() => reject(c.id)} className="btn-ghost text-sm text-red-600" data-testid={`recharge-reject-${c.id}`}><X className="w-4 h-4" /> Reject</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
